import { CalculationSetting } from "../interfaces/ProvidersInterfaces";

type SettingKind = "percent" | "currency" | "days" | "number";

interface SettingMeta {
  group: string;
  label: string;
  kind: SettingKind;
}

export interface FormattedSetting {
  id: number | string;
  key: string;
  label: string;
  display: string;
  description?: string;
}

export interface SettingGroup {
  name: string;
  items: FormattedSetting[];
}

const GROUP_ORDER = ["Fees", "Down payment", "Rounding", "Quote", "Tier add-ons", "Insurance", "Other"];

const SETTING_META: Record<string, SettingMeta> = {
  dst_percentage: { group: "Fees", label: "DST (variable fee)", kind: "percent" },
  processing_fee_fixed: { group: "Fees", label: "Processing fee", kind: "currency" },
  ci_fee: { group: "Fees", label: "Credit investigation", kind: "currency" },
  transfer_fee: { group: "Fees", label: "Transfer", kind: "currency" },
  min_dp_percentage_tfs_owned: { group: "Down payment", label: "Minimum · TFS-owned", kind: "percent" },
  min_dp_percentage_non_tfs_owned: { group: "Down payment", label: "Minimum · non-TFS-owned", kind: "percent" },
  loan_rounding_step: { group: "Rounding", label: "Loan step", kind: "currency" },
  cashout_rounding_step: { group: "Rounding", label: "Cashout step", kind: "currency" },
  monthly_rounding_step: { group: "Rounding", label: "Monthly amort step", kind: "currency" },
  quote_validity_days: { group: "Quote", label: "Validity", kind: "days" },
  tier_acv_percent_addon: { group: "Tier add-ons", label: "ACV · percent", kind: "percent" },
  tier_acv_fixed_addon: { group: "Tier add-ons", label: "ACV · fixed", kind: "currency" },
  tier_regular_percent_addon: { group: "Tier add-ons", label: "Regular · percent", kind: "percent" },
  tier_regular_fixed_addon: { group: "Tier add-ons", label: "Regular · fixed", kind: "currency" },
  insurance_own_damage_rate: { group: "Insurance", label: "Own damage", kind: "percent" },
  insurance_acts_of_nature_rate: { group: "Insurance", label: "Acts of nature", kind: "percent" },
  insurance_property_damage_fixed: { group: "Insurance", label: "Property damage", kind: "currency" },
  insurance_bodily_injury_fixed: { group: "Insurance", label: "Bodily injury", kind: "currency" },
  insurance_doc_stamps_rate: { group: "Insurance", label: "Doc stamps", kind: "percent" },
  insurance_evat_rate: { group: "Insurance", label: "EVAT", kind: "percent" },
  insurance_lgu_tax_rate: { group: "Insurance", label: "LGU tax", kind: "percent" },
};

const humanize = (key: string) => {
  const text = key.replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const inferKind = (key: string): SettingKind => {
  if (/percent|_rate$/.test(key)) return "percent";
  if (/fee|fixed|step|amount/.test(key)) return "currency";
  if (/days/.test(key)) return "days";
  return "number";
};

const trimDecimals = (value: number, digits: number) => String(parseFloat(value.toFixed(digits)));

export const formatSettingValue = (value: number, kind: SettingKind) => {
  const numeric = Number(value);

  if (Number.isNaN(numeric)) return "—";
  if (kind === "percent") return `${trimDecimals(numeric * 100, 4)}%`;
  if (kind === "currency") return `₱${numeric.toLocaleString()}`;
  if (kind === "days") return `${numeric} day${numeric === 1 ? "" : "s"}`;
  return numeric.toLocaleString();
};

export const formatRatePercent = (interestValue: number) =>
  `${(Number(interestValue) * 100).toFixed(2)}%`;

// interest_value is the whole interest over the term, so spread it across the months
export const formatMonthlyRatePercent = (interestValue: number, months: number | null, digits = 2) =>
  months ? `${((Number(interestValue) * 100) / months).toFixed(digits)}%` : null;

export const groupCalculationSettings = (settings: CalculationSetting[] = []): SettingGroup[] => {
  const groups = new Map<string, FormattedSetting[]>();

  settings.forEach((setting) => {
    const meta = SETTING_META[setting.setting_key];
    const group = meta?.group ?? "Other";
    const kind = meta?.kind ?? inferKind(setting.setting_key);

    groups.set(group, [
      ...(groups.get(group) ?? []),
      {
        id: setting.calculation_setting_id ?? setting.setting_key,
        key: setting.setting_key,
        label: meta?.label ?? humanize(setting.setting_key),
        display: formatSettingValue(setting.setting_value, kind),
        description: setting.description,
      },
    ]);
  });

  return GROUP_ORDER.filter((name) => groups.has(name)).map((name) => ({
    name,
    items: groups.get(name) ?? [],
  }));
};
